import { useCallback, useEffect, useRef, useState } from "react";
import { store, useStruct, useTick } from "../../plc-sim/store";
import { PanelCanvas } from "../../components/plc-sim/PanelCanvas";
import { LadderView } from "../../components/plc-sim/LadderView";
import { FbdView } from "../../components/plc-sim/FbdView";
import { DevicePalette, Properties } from "../../components/plc-sim/Palette";
import { IoTable } from "../../components/plc-sim/IoTable";
import { IoMapping } from "../../components/plc-sim/IoMapping";
import { ErrorBar } from "../../components/plc-sim/ErrorBar";
import { SimulatorAudio } from "../../components/plc-sim/SimulatorAudio";
import { EXAMPLES } from "../../plc-sim/examples";
import { DEF } from "../../plc-sim/library";
import type { Issue } from "../../plc-sim/diagnostics";

type Board = "panel" | "program";
type RightTab = "props" | "io";

export default function PlcSimulatorPage() {
  useStruct();
  const [board, setBoard] = useState<Board>("panel");
  const [lang, setLang] = useState<"LAD" | "FBD">("LAD");
  const [selected, setSelected] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [selectedWire, setSelectedWire] = useState<string | null>(null);
  const [wireColorId, setWireColorId] = useState("red");
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightPanel, setRightPanel] = useState<RightTab | null>(null);
  const [ladderH, setLadderH] = useState(180);
  const [monitorBox, setMonitorBox] = useState({ w: 360, h: 230 });
  const [monitorMax, setMonitorMax] = useState(false);
  const monitorResize = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const [propsBox, setPropsBox] = useState({ w: 320, h: 360 });
  const [propsMax, setPropsMax] = useState(false);
  const propsResize = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const [runWarn, setRunWarn] = useState(0);
  const monitoring = store.status === "RUN";

  useEffect(() => {
    store.restoreLast();
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const hadDark = html.classList.contains("dark");
    const oldHtmlBg = html.style.backgroundColor;
    const oldBodyBg = body.style.backgroundColor;

    html.classList.remove("dark");
    html.style.backgroundColor = "#ffffff";
    body.style.backgroundColor = "#ffffff";

    return () => {
      if (hadDark) html.classList.add("dark");
      html.style.backgroundColor = oldHtmlBg;
      body.style.backgroundColor = oldBodyBg;
    };
  }, []);

  const focusDevice = useCallback((id: string) => {
    setBoard("panel");
    setSelected(id);
    setRightPanel("props");
    setFocusId(id);
    setTimeout(() => setFocusId(null), 60);
  }, []);

  const selectPanelItem = useCallback((id: string | null) => {
    setSelected(id);
    if (id) {
      setRightPanel("props");
    } else {
      setRightPanel((current) => (current === "props" ? null : current));
    }
  }, []);

  const gotoIssue = useCallback(
    (i: Issue) => {
      if (i.deviceId) focusDevice(i.deviceId);
      else if (i.netId) setBoard("program");
    },
    [focusDevice],
  );

  const startRun = useCallback(
    (force = false) => {
      if (monitoring) {
        store.stop();
        return;
      }
      const errors = store.issues.filter((i) => i.level === "error").length;
      if (errors && !force) {
        setRunWarn(errors);
        return;
      }
      setRunWarn(0);
      setLeftOpen(false);
      setRightPanel(null);
      store.run();
    },
    [monitoring],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = (e.target as HTMLElement)?.tagName;
      if (t === "INPUT" || t === "SELECT" || t === "TEXTAREA") return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        e.shiftKey ? store.redo() : store.undo();
      }
      if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        store.save();
      }
      if (mod && e.key.toLowerCase() === "d" && selected) {
        e.preventDefault();
        store.duplicateDevice(selected);
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedWire) {
          store.removeWire(selectedWire);
          setSelectedWire(null);
        } else if (selected) {
          store.removeDevice(selected);
          setSelected(null);
        }
      }
      if (e.key === "F5") {
        e.preventDefault();
        startRun();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, selectedWire, startRun]);

  const program =
    lang === "LAD" ? <LadderView tool={null} /> : <FbdView />;

  const addDeviceInVisibleArea = (type: string) => {
    const def = DEF[type];
    if (!def) return;

    let x = 120;
    let y = 120;
    const canvas = document.querySelector<SVGSVGElement>('svg[data-panel-canvas="true"]');

    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const vx = Number(canvas.dataset.viewX ?? 0);
      const vy = Number(canvas.dataset.viewY ?? 0);
      const kRaw = Number(canvas.dataset.viewK ?? 1);
      const k = Number.isFinite(kRaw) && kRaw > 0 ? kRaw : 1;

      // Convert the exact centre of the work area currently visible to the
      // user into world coordinates. This keeps new parts in front of the
      // user even after panning or zooming to 300%.
      const cx = (rect.width / 2 - vx) / k;
      const cy = (rect.height / 2 - vy) / k;
      const baseX = cx - def.w / 2;
      const baseY = cy - def.h / 2;

      // Avoid dropping every new part on the exact same pixels when several
      // parts are added in succession; keep the small cascade around the
      // visible centre instead of sending parts to a fixed project location.
      const sx = Math.max(34, Math.min(84, def.w * 0.28));
      const sy = Math.max(34, Math.min(84, def.h * 0.28));
      const offsets: Array<[number, number]> = [
        [0, 0],
        [sx, sy], [-sx, sy], [sx, -sy], [-sx, -sy],
        [2 * sx, 0], [0, 2 * sy], [-2 * sx, 0], [0, -2 * sy],
        [2 * sx, 2 * sy], [-2 * sx, 2 * sy], [2 * sx, -2 * sy], [-2 * sx, -2 * sy],
      ];

      const overlaps = (px: number, py: number) =>
        store.project.devices.some((existing) => {
          const ed = DEF[existing.type];
          if (!ed) return false;
          const pad = 12;
          return (
            px < existing.x + ed.w + pad &&
            px + def.w + pad > existing.x &&
            py < existing.y + ed.h + pad &&
            py + def.h + pad > existing.y
          );
        });

      const spot = offsets.find(([ox, oy]) => !overlaps(baseX + ox, baseY + oy)) ?? offsets[0]!;
      x = baseX + spot[0];
      y = baseY + spot[1];
    }

    const id = store.addDevice(type, x, y);
    if (id) selectPanelItem(id);
    setLeftOpen(false);
  };

  return (
    <div
      dir="rtl"
      className="plc-light flex h-[100dvh] touch-none select-none flex-col overflow-hidden overscroll-none bg-white text-foreground"
    >
      <SimulatorAudio />
      <TopBar
        board={board}
        setBoard={setBoard}
        lang={lang}
        setLang={setLang}
        onRun={() => startRun()}
        rightPanel={rightPanel}
        onToggleRightPanel={(panel) => setRightPanel((current) => (current === panel ? null : panel))}
        paletteOpen={leftOpen}
        onTogglePalette={() => { setBoard("panel"); setLeftOpen((v) => !v); }}
      />

      {runWarn > 0 && (
        <div className="flex items-center gap-2 bg-destructive/10 px-3 py-1 text-[11px] text-destructive">
          <span>يوجد {runWarn} خطأ يمنع التشغيل — راجع لوحة الأخطاء بالأسفل.</span>
          <button className="sim-btn" onClick={() => startRun(true)}>
            تشغيل على أي حال
          </button>
          <button className="sim-btn" onClick={() => setRunWarn(0)}>
            إغلاق
          </button>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1">
        {/* left (start) panel */}
        <aside
          className={`shrink-0 border-l border-border bg-card transition-all ${
            leftOpen ? "w-56 md:w-64" : "w-0 overflow-hidden"
          }`}
        >
          {board === "panel" ? (
            <DevicePalette
              onAdd={addDeviceInVisibleArea}
              wireColorId={wireColorId}
              setWireColorId={setWireColorId}
            />
          ) : (
            <div className="h-full p-3 text-[11px] text-muted-foreground">
              أدوات البرمجة تظهر داخل لوحة البرمجة عند الضغط على خانة أو عنصر.
            </div>
          )}
        </aside>

        <button
          className={`${board === "program" && !leftOpen ? "hidden" : ""} w-4 shrink-0 border-l border-border bg-secondary text-[10px] text-muted-foreground`}
          onClick={() => setLeftOpen((v) => !v)}
          title="طي / إظهار"
        >
          {leftOpen ? "‹" : "›"}
        </button>

        <main className="flex min-w-0 flex-1 flex-col">
          {monitoring ? (
            <div className="relative min-h-0 flex-1">
              <PanelCanvas
                selected={selected}
                setSelected={selectPanelItem}
                selectedWire={selectedWire}
                setSelectedWire={setSelectedWire}
                wireColorId={wireColorId}
                focusId={focusId}
              />

              <div
                className={`absolute z-30 overflow-hidden rounded-lg border border-border bg-background/95 shadow-2xl backdrop-blur ${
                  monitorMax ? "inset-2" : "right-2 top-2"
                }`}
                style={monitorMax ? undefined : { width: monitorBox.w, height: monitorBox.h, maxWidth: "calc(100% - 1rem)", maxHeight: "calc(100% - 1rem)" }}
              >
                <div className="flex h-8 items-center gap-1 border-b border-border bg-card px-2 text-[11px]">
                  <span className="font-semibold text-muted-foreground">مراقبة البرنامج</span>
                  {(["LAD", "FBD"] as const).map((l) => (
                    <button
                      key={l}
                      onClick={() => setLang(l)}
                      className={`rounded px-2 py-0.5 font-mono ${
                        lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                  <button
                    className="sim-btn mr-auto"
                    onClick={() => setMonitorMax((v) => !v)}
                    title={monitorMax ? "تصغير" : "تكبير"}
                  >
                    {monitorMax ? "تصغير" : "تكبير"}
                  </button>
                </div>
                <div className="h-[calc(100%-2rem)] overflow-auto">{program}</div>
                {!monitorMax && (
                  <div
                    className="absolute bottom-0 left-0 h-8 w-8 touch-none cursor-nesw-resize"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch {}
                      monitorResize.current = { x: e.clientX, y: e.clientY, w: monitorBox.w, h: monitorBox.h };
                    }}
                    onPointerMove={(e) => {
                      const r = monitorResize.current;
                      if (!r) return;
                      const maxW = Math.max(240, window.innerWidth - 24);
                      const maxH = Math.max(150, window.innerHeight - 90);
                      const w = Math.min(maxW, Math.max(240, r.w + (r.x - e.clientX)));
                      const h = Math.min(maxH, Math.max(150, r.h + (e.clientY - r.y)));
                      setMonitorBox({ w, h });
                    }}
                    onPointerUp={() => { monitorResize.current = null; }}
                    onPointerCancel={() => { monitorResize.current = null; }}
                    title="اسحب لتغيير الحجم"
                  >
                    <div className="absolute bottom-1 left-1 h-3 w-3 border-b-2 border-l-2 border-primary" />
                  </div>
                )}
              </div>
            </div>
          ) : board === "panel" ? (
            <PanelCanvas
              selected={selected}
              setSelected={selectPanelItem}
              selectedWire={selectedWire}
              setSelectedWire={setSelectedWire}
              wireColorId={wireColorId}
              focusId={focusId}
            />
          ) : (
            program
          )}
        </main>


        {rightPanel === "props" && (
          <aside
            className={`absolute z-40 flex flex-col overflow-hidden rounded-lg border border-border bg-card/95 shadow-xl backdrop-blur ${
              propsMax ? "inset-2" : "left-2 top-2"
            }`}
            style={
              propsMax
                ? undefined
                : {
                    width: propsBox.w,
                    height: propsBox.h,
                    maxWidth: "calc(100% - 1rem)",
                    maxHeight: "calc(100% - 1rem)",
                  }
            }
          >
            <div className="flex h-8 shrink-0 items-center gap-1 border-b border-border bg-card px-2 text-[11px]">
              <span className="font-semibold">خصائص القطعة</span>
              <button
                className="sim-btn mr-auto"
                onClick={() => setPropsMax((v) => !v)}
                title={propsMax ? "تصغير" : "تكبير"}
              >
                {propsMax ? "تصغير" : "تكبير"}
              </button>
              <button className="sim-btn" onClick={() => setRightPanel(null)}>
                إغلاق
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <Properties
                selected={selected}
                onDelete={() => {
                  if (selected) store.removeDevice(selected);
                  selectPanelItem(null);
                }}
              />
              <div className="border-y border-border px-3 py-1.5 text-[11px] font-semibold text-muted-foreground">
                Inputs / Outputs
              </div>
              <IoTable />
            </div>
            {!propsMax && (
              <div
                className="absolute bottom-0 right-0 h-9 w-9 touch-none cursor-nwse-resize"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch {}
                  propsResize.current = { x: e.clientX, y: e.clientY, w: propsBox.w, h: propsBox.h };
                }}
                onPointerMove={(e) => {
                  const r = propsResize.current;
                  if (!r) return;
                  const maxW = Math.max(240, window.innerWidth - 24);
                  const maxH = Math.max(170, window.innerHeight - 90);
                  const w = Math.min(maxW, Math.max(240, r.w + (e.clientX - r.x)));
                  const h = Math.min(maxH, Math.max(170, r.h + (e.clientY - r.y)));
                  setPropsBox({ w, h });
                }}
                onPointerUp={() => { propsResize.current = null; }}
                onPointerCancel={() => { propsResize.current = null; }}
                title="اسحب لتغيير حجم الخصائص"
              >
                <div className="absolute bottom-1 right-1 h-3 w-3 border-b-2 border-r-2 border-primary" />
              </div>
            )}
          </aside>
        )}

        {rightPanel === "io" && (
          <aside className="absolute left-2 top-2 z-40 flex max-h-[calc(100%-1rem)] w-[min(22rem,calc(100%-1rem))] flex-col overflow-hidden rounded-lg border border-border bg-card/95 shadow-xl backdrop-blur">
            <div className="flex shrink-0 items-center justify-between border-b border-border px-2 py-1.5">
              <span className="text-[11px] font-semibold">جدول I/O</span>
              <button className="sim-btn" onClick={() => setRightPanel(null)}>
                إغلاق
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <IoMapping onFocus={focusDevice} />
            </div>
          </aside>
        )}

      </div>

      <StatusBar onGoto={gotoIssue} />
    </div>
  );
}

function SplitHandle({
  height,
  setHeight,
}: {
  height: number;
  setHeight: (h: number) => void;
}) {
  const start = useRef<{ y: number; h: number } | null>(null);
  return (
    <div
      className="h-2 shrink-0 cursor-row-resize bg-secondary"
      onPointerDown={(e) => {
        (e.target as Element).setPointerCapture(e.pointerId);
        start.current = { y: e.clientY, h: height };
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const next = start.current.h - (e.clientY - start.current.y);
        setHeight(Math.min(window.innerHeight * 0.55, Math.max(110, next)));
      }}
      onPointerUp={() => (start.current = null)}
      onPointerCancel={() => (start.current = null)}
    >
      <div className="mx-auto mt-[3px] h-[2px] w-16 rounded bg-border" />
    </div>
  );
}

function LiveChips() {
  useTick();
  return (
    <span className="mr-auto font-mono text-[10px] text-muted-foreground">
      Scan {store.scanMs.toFixed(2)} ms · Cycles {store.scanCount}
    </span>
  );
}

function TopBar({
  board,
  setBoard,
  lang,
  setLang,
  onRun,
  rightPanel,
  onToggleRightPanel,
  paletteOpen,
  onTogglePalette,
}: {
  board: Board;
  setBoard: (b: Board) => void;
  lang: "LAD" | "FBD";
  setLang: (l: "LAD" | "FBD") => void;
  onRun: () => void;
  rightPanel: RightTab | null;
  onToggleRightPanel: (panel: RightTab) => void;
  paletteOpen: boolean;
  onTogglePalette: () => void;
}) {
  useTick();
  const running = store.status === "RUN";
  return (
    <header className="relative z-50 flex flex-wrap items-center gap-1.5 border-b border-border bg-card px-2 py-1.5">
      <span className="ml-2 font-mono text-[13px] font-bold text-primary">PLC LAB</span>
      <a
        href={`${__BASE_PATH__.replace(/\/$/, "") || ""}/`}
        className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-bold text-foreground hover:bg-muted"
        title="الصفحة الرئيسية"
      >
        <img
          src={`${__BASE_PATH__.replace(/\/$/, "") || ""}/engineer-logo.png`}
          alt="شعار المهندس"
          className="h-5 w-5 rounded-full object-contain"
        />
        <span>الصفحة الرئيسية</span>
      </a>

      <div className="flex rounded-md border border-border p-0.5">
        {(
          [
            ["panel", "لوحة العمل"],
            ["program", "لوحة البرمجة"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setBoard(id)}
            className={`rounded px-2.5 py-1 text-[11px] ${
              board === id ? "bg-primary text-primary-foreground" : "text-muted-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <button
        className={`sim-btn ${paletteOpen ? "border-primary text-primary" : ""}`}
        onClick={onTogglePalette}
      >
        لوحة القطع
      </button>

      {board === "program" && !running && (
        <div className="flex rounded-md border border-border p-0.5">
          {(["LAD", "FBD"] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`rounded px-2.5 py-1 font-mono text-[11px] ${
                lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      <button
        className={`sim-btn ${rightPanel === "props" ? "border-primary text-primary" : ""}`}
        onClick={() => onToggleRightPanel("props")}
      >
        خصائص
      </button>
      <button
        className={`sim-btn ${rightPanel === "io" ? "border-primary text-primary" : ""}`}
        onClick={() => onToggleRightPanel("io")}
      >
        جدول I/O
      </button>

      <div className="mx-1 h-5 w-px bg-border" />

      <button
        className={`sim-btn ${running ? "bg-[var(--lad-live)] text-white" : ""}`}
        onClick={onRun}
      >
        {running ? "■ إيقاف المراقبة" : "▶ RUN / مراقبة"}
      </button>
      <button className="sim-btn" onClick={() => store.reset()}>
        إعادة تشغيل
      </button>
      <button className="sim-btn" onClick={() => store.undo()}>
        تراجع
      </button>
      <button className="sim-btn" onClick={() => store.redo()}>
        إعادة
      </button>
      <button className="sim-btn" onClick={() => store.save()}>
        حفظ
      </button>
      <button className="sim-btn" onClick={() => store.newProject()}>
        مشروع جديد
      </button>
      <ExamplesMenu />
      <button className="sim-btn" onClick={() => store.exportProject()}>
        تصدير
      </button>
      <label className="sim-btn cursor-pointer">
        استيراد
        <input
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void store.importProject(f);
          }}
        />
      </label>
      <OpenMenu />
    </header>
  );
}

function ExamplesMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button className="sim-btn" onClick={() => setOpen((v) => !v)}>
        أمثلة جاهزة
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-[90] w-56 rounded-md border border-border bg-card p-1 shadow-lg">
          {EXAMPLES.map((ex) => (
            <button
              key={ex.id}
              className="block w-full rounded px-2 py-1.5 text-right text-[11px] hover:bg-secondary"
              onClick={() => {
                store.loadExample(ex.id);
                setOpen(false);
              }}
            >
              {ex.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OpenMenu() {
  useStruct();
  const [open, setOpen] = useState(false);
  const list = typeof window === "undefined" ? [] : store.list();
  return (
    <div className="relative">
      <button className="sim-btn" onClick={() => setOpen((v) => !v)}>
        فتح مشروع
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-[90] w-56 rounded-md border border-border bg-card p-1 shadow-lg">
          {list.length === 0 && (
            <p className="px-2 py-1.5 text-[11px] text-muted-foreground">لا توجد مشاريع محفوظة.</p>
          )}
          {list.map((p) => (
            <div key={p.id} className="flex items-center gap-1">
              <button
                className="flex-1 rounded px-2 py-1.5 text-right text-[11px] hover:bg-secondary"
                onClick={() => {
                  store.load(p);
                  setOpen(false);
                }}
              >
                {p.name}
              </button>
              <button
                className="px-2 text-[11px] text-destructive"
                onClick={() => store.remove(p.id)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBar({ onGoto }: { onGoto: (i: Issue) => void }) {
  useTick();
  return (
    <div className="shrink-0">
      <ErrorBarWrapper onGoto={onGoto} />
    </div>
  );
}

function ErrorBarWrapper({ onGoto }: { onGoto: (i: Issue) => void }) {
  useTick();
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 border-t border-border bg-card px-3 py-1 font-mono text-[10px] text-muted-foreground">
        <span
          className="rounded px-2 py-0.5 text-white"
          style={{ background: store.status === "RUN" ? "var(--lad-live)" : "#7f8b96" }}
        >
          {store.status}
        </span>
        <span>Scan {store.scanMs.toFixed(2)} ms</span>
        <span className="hidden sm:inline">Cycles {store.scanCount}</span>
        <span className="hidden sm:inline">{store.project.devices.length} قطعة</span>
        <span className="hidden sm:inline">{store.project.wires.length} سلك</span>
        <ErrorBar onGoto={onGoto} />
        <span className="mr-auto truncate">{store.project.name}</span>
      </div>
    </div>
  );
}
